import { useCallback, useEffect, useState } from 'react'
import { faqRepository } from '@/data/faqs/repositories/faq_repository_impl'
import type { Faq } from '@/domain/faqs/entities/faq.entity'
import { DeleteFaqUseCase } from '@/domain/faqs/usecases/delete_faq.usecase'
import { ListFaqsUseCase } from '@/domain/faqs/usecases/list_faqs.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import { matchFaq } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'
import {
  initialFaqsDeleteDialogState,
  type FaqSortField,
  type FaqsDeleteDialogState,
} from '../models/faqs_ui_state'

const defaultListFaqs = new ListFaqsUseCase(faqRepository)
const defaultDeleteFaq = new DeleteFaqUseCase(faqRepository)

export interface UseFaqsListViewModelDeps {
  listFaqs?: ListFaqsUseCase
  deleteFaq?: DeleteFaqUseCase
}

export function useFaqsListViewModel(deps: UseFaqsListViewModelDeps = {}) {
  const listFaqs = deps.listFaqs ?? defaultListFaqs
  const deleteFaq = deps.deleteFaq ?? defaultDeleteFaq

  const [faqs, setFaqs] = useState<Faq[]>([])
  const isLoading = false
  const [deleteDialog, setDeleteDialog] = useState<FaqsDeleteDialogState>(
    initialFaqsDeleteDialogState
  )
  const [isDeleting, setIsDeleting] = useState(false)

  const { table, actions: tableActions } = useTableQueryState({
    items: faqs,
    matchesSearch: matchFaq,
  })

  const refresh = useCallback(async () => {
    const list = await listFaqs.execute()
    setFaqs(list)
  }, [listFaqs])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openDeleteDialog = useCallback((faqId: string, faqQuestion: string) => {
    setDeleteDialog({ isOpen: true, faqId, faqQuestion })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(initialFaqsDeleteDialogState)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.faqId) return
    try {
      setIsDeleting(true)
      await deleteFaq.execute(deleteDialog.faqId)
      setFaqs(prev => prev.filter(faq => faq.id !== deleteDialog.faqId))
      closeDeleteDialog()
    } catch (error: unknown) {
      logError('Failed to delete FAQ:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to delete FAQ. Please try again.'))
    } finally {
      setIsDeleting(false)
    }
  }, [closeDeleteDialog, deleteDialog.faqId, deleteFaq])

  const handleSort = useCallback(
    (field: FaqSortField) => tableActions.handleSort(field),
    [tableActions]
  )

  return {
    state: {
      faqs,
      filteredFaqs: table.filteredItems,
      paginatedFaqs: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    isDeleting,
    actions: {
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
